import React, { useState } from "react";
import ChatModal from "./ChatModal";

interface UserProfilePictureProps {
  user: {
    userId: string;
    email: string;
    displayName: string;
    photoURL?: string;
  };
  currentUser: {
    userId: string;
    email: string;
    displayName: string;
  };
  size?: number;
  showChatOnClick?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const UserProfilePicture: React.FC<UserProfilePictureProps> = ({
  user,
  currentUser,
  size = 40,
  showChatOnClick = true,
  className,
  style,
}) => {
  const [showChat, setShowChat] = useState(false);

  const handleClick = () => {
    if (showChatOnClick && user.userId !== currentUser.userId) {
      setShowChat(true);
    }
  };

  const profilePictureStyle: React.CSSProperties = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: "50%",
    backgroundColor: "#3b82f6",
    backgroundImage: user.photoURL ? `url(${user.photoURL})` : "none",
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontWeight: "bold",
    position: "relative",
    cursor:
      showChatOnClick && user.userId !== currentUser.userId
        ? "pointer"
        : "default",
    border: "2px solid #e0e0e0",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    ...style,
  };

  const overlayStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    borderRadius: "50%",
    display: user.photoURL ? "block" : "none",
  };

  const hoverStyle: React.CSSProperties = {
    transform: "scale(1.05)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
  };

  return (
    <>
      <div
        className={className}
        style={profilePictureStyle}
        onClick={handleClick}
        onMouseEnter={(e) => {
          if (showChatOnClick && user.userId !== currentUser.userId) {
            Object.assign(e.currentTarget.style, hoverStyle);
          }
        }}
        onMouseLeave={(e) => {
          if (showChatOnClick && user.userId !== currentUser.userId) {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "none";
          }
        }}
        title={
          showChatOnClick && user.userId !== currentUser.userId
            ? `Click to chat with ${user.displayName}`
            : user.displayName
        }
      >
        {/* Semi-transparent overlay to ensure text visibility on any image */}
        <div style={overlayStyle}></div>

        {/* Show initials if no photo */}
        {!user.photoURL && (
          <span style={{ fontSize: `${size * 0.4}px`, zIndex: 1 }}>
            {user.displayName?.charAt(0).toUpperCase() ||
              user.email?.charAt(0).toUpperCase() ||
              "?"}
          </span>
        )}

        {/* Chat indicator for clickable profiles */}
        {showChatOnClick && user.userId !== currentUser.userId && (
          <div
            style={{
              position: "absolute",
              bottom: "-2px",
              right: "-2px",
              width: `${size * 0.3}px`,
              height: `${size * 0.3}px`,
              backgroundColor: "#10b981",
              borderRadius: "50%",
              border: "2px solid white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: `${size * 0.2}px`,
              color: "white",
            }}
          >
            💬
          </div>
        )}
      </div>

      {showChat && (
        <ChatModal
          senderEmail={currentUser.email}
          receiverEmail={user.email}
          receiverId={user.userId}
          receiverDisplayName={user.displayName}
          onClose={() => setShowChat(false)}
        />
      )}
    </>
  );
};

export default UserProfilePicture;
